/**
 * Figma Node IDs:
 * Design Frame: 381:53112
 * Instance IDs: I381:53171;381:53114, I381:53171;381:53115, I381:53171;381:53116, I381:53171;381:53117, I381:53171;381:53118, I381:53171;381:53119, I381:53171;381:53120, I381:53171;381:53121, I381:53171;381:53122, I381:53171;381:53123, I381:53171;381:53124, I381:53171;381:53125, I381:53171;381:53126, I381:53171;381:53127, I381:53171;381:53128, I381:53171;381:53129, I381:53171;381:53130, I381:53171;381:53131, I381:53171;381:53132, I381:53171;381:53134, I381:53171;381:53136, I381:53171;381:53138, I381:53171;381:53140, I381:53171;381:53142, I381:53181;381:53152, I381:53181;381:53153, I381:53191;381:53160, I381:53191;381:53161, I381:53191;381:53162, I381:53191;381:53163, I381:53191;381:53165, I1517:82292;1794:18783, I1517:82470;1794:18783, I1517:82415;1794:18783, I1517:82525;1517:82946, I1252:9857;74:2296
 * Composite: true
 * Last Updated: 2024-12-23
 * Generated by: Claude Code figma-component command
 */
import * as React from "react";
import * as Toast from "@radix-ui/react-toast";
import { defineMessages, useIntl } from "react-intl";

import { RichText } from "@learningblocks/shared-components/lib/RichText";
import {
  GuidanceIcon,
  WarningIcon,
  ContaminationIcon,
  CircleX,
} from "@labxchange/icons";

import { CustomProgressBar } from "./CustomProgressBar";
import * as styles from "./Snackbar.css";
import { useSnackbarRegistry } from "./SnackbarProvider";

const messages = defineMessages({
  closeAlert: {
    id: "snackbar.closeAlert",
    defaultMessage: "Close alert",
    description: "Aria label for the close button in snackbar",
  },
});

export const SnackbarVariant = {
  WARNING: "Warning",
  CONTAMINATION: "Contamination",
  GUIDANCE: "Guidance",
} as const;

export type SnackbarVariantValue =
  (typeof SnackbarVariant)[keyof typeof SnackbarVariant];

interface SnackbarConfig {
  IconComponent: React.ComponentType<{ className?: string }>;
  iconContainerClass: string;
  snackbarTypeClass: string;
}

const SNACKBAR_CONFIG: Record<SnackbarVariantValue, SnackbarConfig> = {
  [SnackbarVariant.WARNING]: {
    IconComponent: WarningIcon,
    iconContainerClass: styles.snackbarWarningIconContainer,
    snackbarTypeClass: styles.snackbarWarning,
  },
  [SnackbarVariant.CONTAMINATION]: {
    IconComponent: ContaminationIcon,
    iconContainerClass: styles.snackbarContaminationIconContainer,
    snackbarTypeClass: styles.snackbarContamination,
  },
  [SnackbarVariant.GUIDANCE]: {
    IconComponent: GuidanceIcon,
    iconContainerClass: styles.snackbarGuidanceIconContainer,
    snackbarTypeClass: styles.snackbarGuidance,
  },
};

export interface SnackbarProps {
  variant: SnackbarVariantValue;
  message: string;
  equation?: boolean;
  /**
   * Controls whether the snackbar is visible. When `true`, the snackbar is displayed.
   * @default true
   */
  open?: boolean;
  /**
   * Callback fired when the open state changes. Called with `false` when:
   * - The close button is clicked
   * - The toast duration expires (controlled by `duration`)
   * - The user swipes to dismiss the toast
   * - The Escape key is pressed while the toast has focus
   */
  onOpenChange?: (open: boolean) => void;
  duration?: number;
}

const Snackbar: React.FC<SnackbarProps> = ({
  variant,
  message,
  equation = false,
  open = true,
  onOpenChange,
  duration = 8000,
}) => {
  const intl = useIntl();
  const [isPaused, setIsPaused] = React.useState(false);
  const registry = useSnackbarRegistry();
  const idRef = React.useRef<string>(
    `snackbar-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  );

  React.useEffect(() => {
    if (open && registry) {
      registry.register(idRef.current);
    }
    return () => {
      if (registry) {
        registry.unregister(idRef.current);
      }
    };
  }, [open, registry]);

  const isEarliest = registry ? registry.isEarliest(idRef.current) : true;
  const hasMultiple = registry ? registry.hasMultiple : false;
  const showProgressBar = isEarliest && hasMultiple;

  const handleClose = React.useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handlePause = React.useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleResume = React.useCallback(() => {
    setIsPaused(false);
  }, []);

  const config =
    SNACKBAR_CONFIG[variant] || SNACKBAR_CONFIG[SnackbarVariant.GUIDANCE];
  const { IconComponent, iconContainerClass, snackbarTypeClass } = config;

  const containerClasses = `${styles.snackbarContainer} ${snackbarTypeClass}`;

  return (
    <Toast.Root
      className={styles.toastRoot}
      open={open}
      onOpenChange={onOpenChange}
      duration={Infinity}
      onPause={handlePause}
      onResume={handleResume}
    >
      <div className={containerClasses}>
        <div
          className={`${styles.snackbarIconContainer} ${iconContainerClass} ${showProgressBar ? styles.snackbarIconContainerWithProgress : ""}`}
        >
          <IconComponent className={styles.snackbarIconImage} />
        </div>

        <div className={styles.snackbarContentWrapper}>
          <Toast.Description className={styles.snackbarTextContainer}>
            <div className={styles.snackbarTextMessage}>
              <RichText text={message} equation={equation} />
            </div>
          </Toast.Description>

          <Toast.Close
            className={styles.snackbarCloseButton}
            aria-label={intl.formatMessage(messages.closeAlert)}
          >
            <CircleX className={styles.snackbarCloseIcon} aria-hidden="true" />
          </Toast.Close>
        </div>
        {isEarliest && (
          <div aria-hidden="true">
            <CustomProgressBar
              hidden={!showProgressBar}
              animationDuration={duration}
              isPaused={isPaused}
              onClose={handleClose}
              type={variant}
            />
          </div>
        )}
      </div>
    </Toast.Root>
  );
};

export default Snackbar;
export { SnackbarProvider, useSnackbar, addSnackbar } from "./SnackbarProvider";
